import { Link } from 'react-router-dom';
import { Camera } from 'lucide-react';
import { AccountingBadge } from './AccountingBadge';
import { StatusBadge } from './StatusBadge';
import { ScheduleTile } from './ScheduleTile';
import { AssigneeTag, CourtesyCarTag, NeedsDetailsMark } from './JobTags';
import { jobSchedule } from '../lib/schedule';
import { formatMoneyPence } from '../lib/format';
import { useJobFinance, useJobFinanceStatus, withJobFinance } from '../lib/jobFinance';
import type { Job } from '../types/models';

export function JobRow({ job, to, showFinancials = false }: { job: Job; to: string; showFinancials?: boolean }) {
  const photos = job.imageCount ?? job.typedImages?.length ?? job.imageUrls?.length ?? 0;
  const vehicleLine = [[job.make, job.model].filter(Boolean).join(' '), job.colour].filter(Boolean).join(' · ');
  job = withJobFinance(job, useJobFinance(job.id));
  const financeStatus = useJobFinanceStatus();
  return (
    <article className={`job-row rounded-[8px] border border-hair bg-elev transition-colors hover:border-strong ${job.state === 'cancelled' ? 'opacity-[0.72]' : ''}`}>
      <div className="job-row-content">
        <ScheduleTile schedule={jobSchedule(job)} />
        <div className="job-row-identity">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2">
            <Link to={to} className="reg break-words text-[17px] text-ink after:absolute after:inset-0 after:rounded-[8px] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-accent">
              {job.registrationNumber || 'No reg'}
            </Link>
            <NeedsDetailsMark job={job} />
            <span className="inline-flex items-center gap-1 font-mono text-[13px] text-ink3" aria-label={`${photos} ${photos === 1 ? 'photo' : 'photos'}`}>
              <Camera size={13} aria-hidden="true" />{photos}
            </span>
          </div>
          <div className="break-words text-[15px] text-ink2">{vehicleLine || 'Registration only'}</div>
        </div>
        <div className="job-row-context">
          <div><span className="text-[12.5px] text-ink3">Customer</span><span className="job-row-context-value text-[14.5px] text-ink">{job.customerName || 'Not set'}</span></div>
          <div><span className="mb-0.5 block text-[12.5px] text-ink3">Assigned</span><AssigneeTag job={job} /></div>
        </div>
        <div className="job-row-status">
          {job.courtesyCarRequired && <CourtesyCarTag compact />}
          <StatusBadge status={job.state} size="sm" />
          {showFinancials && financeStatus.ready && (job.state === 'completed' || job.state === 'archived' || job.accountingStatus) && <AccountingBadge status={job.accountingStatus} size="sm" />}
          {showFinancials && financeStatus.ready && job.pricing && <span className="break-words font-mono text-[13px] text-ink2">{formatMoneyPence(job.pricing.amountPence, job.pricing.currency)}</span>}
          {showFinancials && !financeStatus.ready && <span className="text-[12px] text-ink3">{financeStatus.error ? 'Prices unavailable' : 'Loading prices…'}</span>}
        </div>
      </div>
    </article>
  );
}

export function JobRowSkeleton() {
  return <div className="flex items-center gap-4 rounded-[8px] border border-hair bg-elev px-3.5 py-3">
    <div className="skeleton h-[56px] w-[62px] shrink-0 rounded-[8px]" />
    <div className="min-w-0 flex-1 space-y-2"><div className="skeleton h-4 w-28 max-w-full rounded-md" /><div className="skeleton h-3.5 w-52 max-w-full rounded-md" /></div>
  </div>;
}
